import { ErrorResponseSchema } from '@packages/types';
let bridge = null;
let apiUrl = '';
export function configureApi(config) {
    bridge = config.bridge;
    apiUrl = config.apiUrl;
}
function getBridge() {
    if (!bridge) {
        throw new Error('API not configured. Call configureApi() before making requests.');
    }
    return bridge;
}
export function getApiUrl() {
    if (!apiUrl) {
        throw new Error('API not configured. Call configureApi() before making requests.');
    }
    return apiUrl;
}
export function getApiBridge() {
    return getBridge();
}
export class ApiError extends Error {
    status;
    code;
    retryable;
    details;
    context;
    constructor(status, code, message, retryable = false, details, context) {
        super(message);
        this.status = status;
        this.code = code;
        this.retryable = retryable;
        this.details = details;
        this.context = context;
        this.name = 'ApiError';
    }
}
/**
 * HTTP statuses that indicate a transient infrastructure failure (the request did
 * not reach a healthy application instance) rather than an application error. Cloud
 * Run returns these during a cold-start scale-up before new instances are ready.
 * They are safe to retry; a 500 is excluded because the app ran and may have executed.
 */
const TRANSIENT_INFRA_STATUSES = new Set([502, 503, 504]);
/**
 * Shared session-expired path. The pre-flight expiry check and the 401
 * response handler funnel through here so both produce identical behaviour:
 * clear the stored token, trigger the platform redirect, throw the same
 * error. Caller is responsible for gating on `skipAuth` / `skipAuthErrorHandling`.
 */
function handleSessionExpired(b) {
    b.removeToken();
    b.onUnauthorized();
    throw new ApiError(401, 'UNAUTHORIZED', 'Session expired');
}
function parseContentDispositionParameters(header) {
    const parameters = new Map();
    let index = header.indexOf(';');
    if (index === -1)
        return parameters;
    index += 1;
    while (index < header.length) {
        while (index < header.length && (header[index] === ';' || /\s/.test(header[index]))) {
            index += 1;
        }
        const nameStart = index;
        while (index < header.length && header[index] !== '=' && header[index] !== ';') {
            index += 1;
        }
        const name = header.slice(nameStart, index).trim().toLowerCase();
        if (!name || header[index] !== '=') {
            while (index < header.length && header[index] !== ';')
                index += 1;
            continue;
        }
        index += 1;
        let value = '';
        if (header[index] === '"') {
            index += 1;
            while (index < header.length) {
                const char = header[index];
                if (char === '\\' && index + 1 < header.length) {
                    value += header[index + 1];
                    index += 2;
                    continue;
                }
                if (char === '"') {
                    index += 1;
                    break;
                }
                value += char;
                index += 1;
            }
        }
        else {
            const valueStart = index;
            while (index < header.length && header[index] !== ';')
                index += 1;
            value = header.slice(valueStart, index).trim();
        }
        parameters.set(name, value);
        while (index < header.length && header[index] !== ';')
            index += 1;
    }
    return parameters;
}
function decodeRfc5987Filename(value) {
    const firstQuote = value.indexOf("'");
    const secondQuote = value.indexOf("'", firstQuote + 1);
    if (firstQuote <= 0 || secondQuote === -1)
        return null;
    const charset = value.slice(0, firstQuote).toLowerCase();
    if (charset !== 'utf-8')
        return null;
    const encodedFilename = value.slice(secondQuote + 1);
    try {
        return decodeURIComponent(encodedFilename);
    }
    catch {
        return null;
    }
}
export function filenameFromContentDisposition(header) {
    if (!header)
        return null;
    const parameters = parseContentDispositionParameters(header);
    const encodedFilename = parameters.get('filename*');
    if (encodedFilename != null) {
        return decodeRfc5987Filename(encodedFilename);
    }
    return parameters.get('filename') ?? null;
}
export function filenameFromResponse(response, fallback) {
    return filenameFromContentDisposition(response.headers.get('Content-Disposition')) ?? fallback;
}
export async function downloadFile(path, fallbackFilename) {
    const response = await apiRaw(path);
    const filename = filenameFromResponse(response, fallbackFilename);
    const blob = await response.blob();
    return { blob, filename };
}
export async function parseErrorResponse(response) {
    try {
        const json = await response.json();
        const parsed = ErrorResponseSchema.safeParse(json);
        if (parsed.success) {
            const { code, message, retryable, details, context } = parsed.data.error;
            // Build a descriptive message that includes validation details when present
            let fullMessage = message;
            if (details && details.length > 0) {
                const detailLines = details.map((d) => `${d.field}: ${d.message}`).join('; ');
                fullMessage = `${message}: ${detailLines}`;
            }
            return new ApiError(response.status, code, fullMessage, retryable, details, context);
        }
    }
    catch {
        // Fall through to generic error
    }
    // No parseable app error body (e.g. a Cloud Run infrastructure 502/503/504 with a
    // plain-text body during a scale-up / cold start). These mean the request never
    // reached a healthy instance, so nothing executed - they are transient and safe to
    // retry. Mark them retryable so React Query retries the query rather than surfacing
    // a hard error to the user. A 500 (our app ran and threw) is deliberately NOT here:
    // it may have partially executed, so blind retry is unsafe.
    return new ApiError(response.status, 'UNKNOWN', response.statusText, TRANSIENT_INFRA_STATUSES.has(response.status));
}
/**
 * Typed fetch wrapper for the platform API. The schema's input type is open (`unknown`)
 * so a response schema may default or transform fields on the way in; the result is the
 * schema's output.
 */
export async function api(path, schema, options = {}) {
    const { method = 'GET', body, headers = {}, skipAuth = false, skipAuthErrorHandling = false, keepalive } = options;
    const b = getBridge();
    const requestHeaders = {
        ...headers,
    };
    // Only set Content-Type if there's a body to avoid Fastify empty body errors
    if (body) {
        requestHeaders['Content-Type'] = 'application/json';
    }
    if (!skipAuth) {
        const token = b.getToken();
        if (token) {
            // Pre-flight expiry check: if the JWT has already lapsed there is no
            // value in round-tripping to the server just to get a 401 back. Short
            // circuit so the user sees a clean redirect instead of an in-flight
            // error and so routine expirations never reach the Sentry pipeline.
            if (!skipAuthErrorHandling && !b.hasValidSessionToken()) {
                handleSessionExpired(b);
            }
            requestHeaders['Authorization'] = `Bearer ${token}`;
        }
    }
    const response = await fetch(`${apiUrl}${path}`, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        keepalive,
    });
    if (response.status === 401 && !skipAuth && !skipAuthErrorHandling) {
        handleSessionExpired(b);
    }
    if (response.status === 402) {
        b.onPaymentRequired();
        throw new ApiError(402, 'PAYMENT_REQUIRED', 'An active subscription is required');
    }
    if (!response.ok) {
        throw await parseErrorResponse(response);
    }
    const json = await response.json();
    const result = schema.safeParse(json);
    if (!result.success) {
        const details = result.error.issues.map((e) => ({
            field: e.path.join('.') || '(root)',
            message: e.message,
        }));
        throw new ApiError(0, 'PARSE_ERROR', 'Invalid response from server', false, details);
    }
    return result.data;
}
/**
 * Untyped fetch for endpoints where we don't need response parsing
 */
export async function apiRaw(path, options = {}) {
    const { method = 'GET', body, headers = {}, skipAuth = false, keepalive } = options;
    const b = getBridge();
    const requestHeaders = {
        ...headers,
    };
    // Only set Content-Type if there's a body to avoid Fastify empty body errors
    if (body) {
        requestHeaders['Content-Type'] = 'application/json';
    }
    if (!skipAuth) {
        const token = b.getToken();
        if (token) {
            if (!b.hasValidSessionToken()) {
                handleSessionExpired(b);
            }
            requestHeaders['Authorization'] = `Bearer ${token}`;
        }
    }
    const response = await fetch(`${apiUrl}${path}`, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        keepalive,
    });
    if (response.status === 401 && !skipAuth) {
        handleSessionExpired(b);
    }
    if (!response.ok) {
        throw await parseErrorResponse(response);
    }
    return response;
}
/**
 * Fire-and-forget POST for identity-free telemetry. The body is a JSON string with no
 * `headers` at all, so the browser sends `text/plain` and the request stays CORS-simple (no
 * preflight, which would not reliably survive a navigation). `credentials: 'omit'` keeps
 * cookies off it and `keepalive` lets it outlive the page. It never reads the session token,
 * so no identity can ride along. A non-2xx response rejects with the same `ApiError` as `api()`.
 */
export async function apiBeacon(path, body) {
    const response = await fetch(`${getApiUrl()}${path}`, {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'omit',
        keepalive: true,
    });
    if (!response.ok) {
        throw await parseErrorResponse(response);
    }
}
